import { useEffect, useState } from "react";
import { ref, get } from "firebase/database";
import { database } from "../../config/firebaseConfig";
import { useRemote } from "../../hooks/useRemote";
import useGlobalStore from "../../store/global.store";

const LS_KEY = "wsAnnounceLastSeen";
const DEFAULT_HEADER = "What's New On Your WaitSign!";

const WSANN_STYLE = `
  .wsann-root {
    position: fixed; inset: 0; z-index: 99999;
    background: rgba(1, 2, 25, 0.80);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Lato', sans-serif; user-select: none;
  }
  .wsann-card {
    position: relative;
    width: 1000px; max-width: 94vw;
    background: linear-gradient(180deg, #6bbdf5 0%, #a5d9fa 55%, #e8f6ff 100%);
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    padding: 36px 40px 0;
    display: flex; align-items: flex-end; gap: 28px;
    overflow: hidden;
  }
  .wsann-cast {
    height: 440px; width: auto; flex-shrink: 0;
    display: block;
  }
  .wsann-right {
    flex: 1; display: flex; flex-direction: column;
    align-items: stretch; gap: 20px; padding-bottom: 28px;
  }
  .wsann-header {
    font-family: 'Lato', sans-serif;
    font-weight: 900; font-size: 32px;
    color: #1a2542; letter-spacing: 0.5px;
    line-height: 1.1;
  }
  .wsann-bubble {
    position: relative;
    background: #ffffff;
    border: 3px solid #1a2542;
    border-radius: 18px;
    padding: 24px 28px;
    min-height: 180px;
    display: flex; flex-direction: column; gap: 10px;
  }
  .wsann-bubble::after {
    content: ""; position: absolute; left: -19px; bottom: 44px;
    border-top: 14px solid transparent;
    border-bottom: 14px solid transparent;
    border-right: 16px solid #1a2542;
  }
  .wsann-title {
    font-family: 'Lato', sans-serif;
    color: #1a2542; font-weight: 900; font-size: 30px; line-height: 1.15;
  }
  .wsann-body {
    font-family: 'Lato', sans-serif;
    color: #33415c; font-weight: 500; font-size: 23px; line-height: 1.4;
    white-space: pre-line;
  }
  .wsann-footer {
    display: flex; align-items: center; justify-content: space-between;
  }
  .wsann-dots { display: flex; gap: 8px; }
  .wsann-dot {
    width: 11px; height: 11px; border-radius: 50%;
    background: rgba(26,37,66,0.25);
  }
  .wsann-dot.on { background: #1a2542; }
  .wsann-btn {
    background: #007bff; color: #ffffff;
    border: none; border-radius: 999px;
    padding: 13px 40px;
    font-family: 'Lato', sans-serif; font-weight: 900; font-size: 23px;
    letter-spacing: 0.5px; cursor: pointer; outline: none;
    box-shadow: 0 2px 8px rgba(0, 53, 128, 0.35);
    -webkit-tap-highlight-color: transparent;
  }
`;

export default function AnnouncementPopup({ device }) {
  const { updateView } = useRemote();
  const [pages, setPages] = useState(null);
  const [header, setHeader] = useState(DEFAULT_HEADER);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!device?._id) return;
    const lastSeen = parseInt(localStorage.getItem(LS_KEY) || "0", 10) || 0;
    get(ref(database, "config/announcements"))
      .then((snap) => {
        const data = snap.exists() ? snap.val() : null;
        if (!data) return;
        const hasBeacon =
          device?.has_beacon_addon === true ||
          useGlobalStore.getState().hasBeaconAddOn === true;
        if (typeof data.header === "string" && data.header.trim()) {
          setHeader(data.header);
        }
        const list = Object.entries(data)
          .filter(([, v]) => v && typeof v === "object" && (v.title || v.body))
          .map(([id, v]) => ({ id: parseInt(id, 10), ...v }))
          .filter(
            (a) => Number.isFinite(a.id) && a.id > lastSeen && a.active !== false && !(a.hide_for_beacon === true && hasBeacon),
          )
          .sort((a, b) => a.id - b.id);
        if (list.length) setPages(list);
      })
            .catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?._id]);

  // If the announcement currently shown names a page, switch the screen
  // BEHIND the popup to it (e.g. page: "BAND__upgrade" -> beacon promo),
  // so dismissing the popup leaves the user standing on that page.
  useEffect(() => {
    if (!pages) return;
    const p = pages[idx];
    if (p && typeof p.page === "string" && p.page.trim()) {
      updateView(p.page.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, idx]);

  if (!pages) return null;

  const lastName = device?.subscription?.last_name || "";
  const fill = (s) =>
    String(s || "")
      .replace(/\{last_name\}/g, lastName)
      .replace(/\\n/g, "\n")
      .replace(/\{device\}/g, "Display")
      .replace(/ {2,}/g, " ");

  const page = pages[idx];
  const isLast = idx === pages.length - 1;

  const handleButton = (e) => {
    e.stopPropagation();
    if (!isLast) {
      setIdx(idx + 1);
      return;
    }
    const maxId = pages[pages.length - 1].id;
    localStorage.setItem(LS_KEY, String(maxId));
    setPages(null);
  };

  return (
    <div className="wsann-root" onClick={(e) => e.stopPropagation()}>
      <style>{WSANN_STYLE}</style>
      <div className="wsann-card">
        <img className="wsann-cast" src="/static/castmember-one.png" alt="" />
        <div className="wsann-right">
          <div className="wsann-header">{fill(typeof page.header === "string" && page.header.trim() ? page.header : header)}</div>
          <div className="wsann-bubble">
            <div className="wsann-title">{fill(page.title)}</div>
            <div className="wsann-body">{fill(page.body)}</div>
          </div>
          <div className="wsann-footer">
            <div className="wsann-dots">
              {pages.map((p, i) => (
                <div key={p.id} className={`wsann-dot ${i === idx ? "on" : ""}`} />
              ))}
            </div>
            <button className="wsann-btn" onClick={handleButton}>
              {fill(typeof page.button === "string" && page.button.trim() ? page.button : (isLast ? "Awesome!" : "Next"))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
